import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Select, Space, Typography } from 'antd';
import { adminAPI } from '../services/api';

// Manual dates stay in the form when switching modes, for an explicit return to custom rules.
export default function HolidayCalendarEditor({ defaultMode }) {
  const form = Form.useFormInstance();
  const chosenMode = Form.useWatch('holiday_calendar_mode', form);
  const automatic = (chosenMode || defaultMode) === 'auto';
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [syncStatus, setSyncStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!automatic) return;
    let active = true;
    setBusy(true);
    setPreview(null);
    setError('');
    Promise.all([adminAPI.getHolidayCalendar(), adminAPI.getHolidaySync().catch(() => null)]).then(([result, status]) => {
      if (!active) return;
      if (result.code !== 0 || !result.data?.calendar) throw new Error(result.message || '无法获取日历');
      setPreview(result.data);
      setSyncStatus(status?.code === 0 ? status.data : null);
    }).catch(e => { if (active) setError(e?.response?.data?.message || e.message || '无法获取日历'); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [automatic, reload]);
  return <>
    <Form.Item name="holiday_calendar_mode" label="节假日日历" initialValue={defaultMode}>
      <Select options={[{ value: 'auto', label: '自动同步法定节假日（推荐）' }, { value: 'manual', label: '手动维护自定义日历' }]} />
    </Form.Item>
    {automatic ? <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
      <Alert showIcon type={error ? 'error' : 'info'} message={error || (busy ? '正在读取节假日日历…' : '节假日日历由系统自动维护')}
        description={preview ? `覆盖 ${preview.calendar.from} 至 ${preview.calendar.until}；${preview.next_year_pending ? '下一年度日历待更新，取得有效数据后自动延长。' : '已包含下一年度日历。'}预览和保存使用本地已校验日历，后台每天自动更新。周末（含调休补班日）全天闲时。` : '无需填写日期。读取失败时请重试，或明确切换到手动维护。'}
        action={<Button size="small" loading={busy} onClick={() => setReload(v => v + 1)}>刷新日历</Button>} />
      {syncStatus && <Typography.Text type={syncStatus.warning ? 'warning' : 'secondary'}>最近自动同步：{syncStatus.last_success ? new Date(syncStatus.last_success).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' }) : '尚未完成'}（北京时间）{syncStatus.warning ? `；${syncStatus.error ? '后台更新暂时失败，当前已校验日历在覆盖期内仍可使用。' : syncStatus.warning}` : ''}</Typography.Text>}
      {preview?.calendar_updated_at && <Typography.Text type={preview.calendar_stale ? 'warning' : 'secondary'}>日历数据更新时间：{new Date(preview.calendar_updated_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })}（北京时间）。{preview.calendar_stale ? '后台更新暂未完成，已有日历在覆盖期内仍可正常保存。' : '已在服务器保存，无需每次访问外部数据源。'}</Typography.Text>}
      {preview && <Typography.Text type="secondary">节假日：{preview.calendar.dates.join('、')}</Typography.Text>}
    </Space> : <>
      <Alert type="warning" showIcon message="请维护节假日日历" description="周一至周五在两个高峰区间按忙时计费；周末和列出的节假日全天闲时。手动模式不自动更新，超出覆盖期的请求会被拒绝。" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
        <Form.Item name={['time_pricing', 'coveredFrom']} label="日历覆盖开始"><Input type="date" /></Form.Item>
        <Form.Item name={['time_pricing', 'coveredUntil']} label="日历覆盖结束"><Input type="date" /></Form.Item>
      </div>
      <Form.Item name={['time_pricing', 'holidays']} label="节假日日期" extra="每行一个日期，例如 2026-10-01；包含覆盖期内所有需按闲时计费的节假日。"><Input.TextArea rows={4} /></Form.Item>
    </>}
  </>;
}
